import { supabase } from '../../lib/supabase';
import { AppUser } from '../../types';

/**
 * Handles the bridging between Supabase Auth identities and our internal 'users' table.
 */
export const getCurrentProfile = async (): Promise<AppUser | null> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;

    const user = session.user;
    const userEmail = user.email?.toLowerCase().trim();
    
    // EMERGENCY BYPASS: If the DB is recursive, we grant the known admin email 
    // immediate system_admin access so they can run the repair SQL.
    const RESCUE_EMAILS = ['oelroos@hfli.org'];
    const isRescueUser = !!(userEmail && RESCUE_EMAILS.includes(userEmail));

    try {
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', user.id)
        .maybeSingle();

      if (profileError) {
        console.warn("AuthService: DB Profile Error (Potential RLS 500/Recursion)", profileError);
        // If it's a recursion error (42P17), use the virtual profile with rescue logic
        return createVirtualProfile(user, userEmail, isRescueUser);
      }

      if (profile) return profile;
    } catch (dbEx) {
      console.error("AuthService: Critical DB Exception during profile fetch", dbEx);
      return createVirtualProfile(user, userEmail, isRescueUser);
    }

    // Attempt to link by email if unlinked record exists
    if (userEmail) {
      try {
        const { data: unlinked } = await supabase
          .from('users')
          .select('*')
          .ilike('email', userEmail)
          .is('auth_user_id', null)
          .maybeSingle();

        if (unlinked) {
          const { data: linked } = await supabase
            .from('users')
            .update({ auth_user_id: user.id })
            .eq('id', unlinked.id)
            .select()
            .maybeSingle();
          if (linked) return linked;
        }
      } catch (linkEx) {
        console.warn("AuthService: Could not link profile. Falling back.");
      }
    }

    return createVirtualProfile(user, userEmail, isRescueUser);
  } catch (err) {
    console.error("AuthService Global Exception:", err);
    return null;
  }
};

/**
 * Creates a memory-only profile using data from the Auth session.
 */
const createVirtualProfile = (user: any, email: string | undefined, forceAdmin: boolean = false): AppUser => {
  return {
    id: 'virtual-' + user.id,
    auth_user_id: user.id,
    email: email || '',
    full_name: user.user_metadata?.full_name || 'HFLI Personnel (Rescue Mode)',
    role: forceAdmin ? 'system_admin' : (user.app_metadata?.role || 'viewer'),
    is_active: true
  };
};